// src/App.jsx
import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import Navbar from "./components/Navbar";
import Banner from "./components/Banner";
import Trailers from "./pages/Trailers";
import AuthModal from "./components/AuthModal";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "./firebase";
import { doc, onSnapshot } from "firebase/firestore";
import Profile from "./pages/Profile";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Footer from "./components/Footer";
import Blogs from "./pages/Blogs";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Community
import CommunityLayout from "./pages/Community";
import GeneralDiscussion from "./pages/Community/GeneralDiscussion";
import MissionsIndex from "./pages/Community/MissionsIndex";
import MissionDetail from "./pages/Community/Missions/MissionDetail";
import StoryCharactersIndex from "./pages/Community/StoryCharactersIndex";
import StoryCharacterDetail from "./pages/Community/StoryCharacters/CharacterDetail";
import LocationIndex from "./pages/Community/LocationIndex";
import Location from "./pages/Community/Location/LocationDetail";
import WeponsVehicleIndex from "./pages/Community/WeponsVehicleIndex";
import VehiclesDetails from "./pages/Community/Vehicles/VehicleDetail";
import WeaponsDetails from "./pages/Community/Weapons/WeaponDetail";
import TipsTricksIndex from "./pages/Community/TipsTricksIndex";
import TipsTricks from "./pages/Community/TipsTricks/TipTrickDetail";
import MusicRadioIndex from "./pages/Community/MusicRadioIndex";
import MusicRadioDetail from "./pages/Community/MusicRadio/MusicRadioDetail";
import Achievements from "./pages/Community/Achievements";
import News_Updates from "./pages/Community/News_UpdatesIndex";
import Article from "./pages/Community/News_Updates/Article";
// ---------- Tracking Hook ----------
function usePageTracking() {
  const location = useLocation();
  const [analyticsReady, setAnalyticsReady] = useState(null);

  useEffect(() => {
    const waitForAnalytics = async () => {
      const { isSupported, getAnalytics } = await import("firebase/analytics");
      const { app } = await import("./firebase");

      if (await isSupported()) {
        const analyticsInstance = getAnalytics(app);
        setAnalyticsReady(analyticsInstance);
      }
    };
    waitForAnalytics();
  }, []);

  useEffect(() => {
    if (analyticsReady) {
      import("firebase/analytics").then(({ logEvent }) => {
        logEvent(analyticsReady, "page_view", {
          page_path: location.pathname,
        });
      });
    }
  }, [location, analyticsReady]);
}

// ---------- AppContent ----------
function AppContent({ user, setShowAuthModal, showAuthModal }) {
  usePageTracking();

  return (
    <>
      <Navbar user={user} setShowAuthModal={setShowAuthModal} />
      <ToastContainer position="top-center" autoClose={3000} />

      <Routes>
        {/* Home */}
        <Route
          path="/"
          element={
            <>
              <Banner />
              {/* Below banner: side menu + General Discussion by default (embedded mode) */}
              <section id="community-section">
                <CommunityLayout
                  embedded
                  defaultKey="general"
                  user={user}
                  setShowAuthModal={setShowAuthModal}
                />
              </section>
            </>
          }
        />

        {/* Trailers */}
        <Route path="/trailers" element={<Trailers />} />

        {/* Community (full page: left menu + nested routes/Outlet) */}
        {/* Forum / General Discussion */}
        <Route
          path="/forum"
          element={
            <CommunityLayout user={user} setShowAuthModal={setShowAuthModal} />
          }
        >
          <Route
            index
            element={<GeneralDiscussion user={user} setShowAuthModal={setShowAuthModal} />}
          />
        </Route>

        {/* Missions */}
        <Route
          path="/missions"
          element={<CommunityLayout user={user} setShowAuthModal={setShowAuthModal} />}
        >
          <Route index element={<MissionsIndex user={user} />} />
          <Route path=":id" element={<MissionDetail user={user} />} />
        </Route>

        {/* Story & Characters */}
        <Route
          path="/story-characters"
          element={<CommunityLayout user={user} setShowAuthModal={setShowAuthModal} />}
        >
          <Route index element={<StoryCharactersIndex user={user} />} />
          <Route path=":id" element={<StoryCharacterDetail user={user} />} />
        </Route>

        {/* Map Locations */}
        <Route
          path="/map-locations"
          element={<CommunityLayout user={user} setShowAuthModal={setShowAuthModal} />}
        >
          <Route index element={<LocationIndex user={user} />} />
          <Route path=":id" element={<Location user={user} />} />
        </Route>

        {/* Vehicles & Weapons */}
        <Route
          path="/vehicles-weapons"
          element={<CommunityLayout user={user} setShowAuthModal={setShowAuthModal} />}
        >
          <Route index element={<WeponsVehicleIndex user={user} />} />
        </Route>

        <Route
          path="/vehicles/:id"
          element={<CommunityLayout user={user} setShowAuthModal={setShowAuthModal} />}
        >
          <Route index element={<VehiclesDetails />} />
        </Route>

        <Route
          path="/weapons/:id"
          element={<CommunityLayout user={user} setShowAuthModal={setShowAuthModal} />}
        >
          <Route index element={<WeaponsDetails />} />
        </Route>

        {/* Tips & Tricks */}
        <Route
          path="/tips-tricks"
          element={<CommunityLayout user={user} setShowAuthModal={setShowAuthModal} />}
        >
          <Route index element={<TipsTricksIndex />} />
          <Route path=":id" element={<TipsTricks />} />
        </Route>

        {/* Music & Radio */}
        <Route
          path="/music-radio"
          element={<CommunityLayout user={user} setShowAuthModal={setShowAuthModal} />}
        >
          <Route index element={<MusicRadioIndex user={user} />} />
          <Route path=":slugOrId" element={<MusicRadioDetail user={user} />} />
        </Route>

        {/* Achievements */}
        <Route
          path="/achievements"
          element={<CommunityLayout user={user} setShowAuthModal={setShowAuthModal} />}
        >
          <Route index element={<Achievements />} />
        </Route>

        {/* News & Updates */}
        <Route
          path="/news-updates"
          element={<CommunityLayout user={user} setShowAuthModal={setShowAuthModal} />}
        >
          <Route index element={<News_Updates />} />
          <Route path="article/:id" element={<Article />} />
        </Route>

        {/* Profile */}
        <Route
          path="/profile"
          element={user ? <Profile user={user} /> : <Navigate to="/" replace />}
        />

        {/* Static pages */}
        <Route path="/about-us" element={<About />} />
        <Route path="/contact" element={<Contact user={user} />} />
        <Route path="/terms-and-conditions" element={<Terms />} />
        <Route path="/privacy-policy" element={<Privacy />} />
        <Route path="/blogs" element={<Blogs />} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
    </>
  );
}

// ---------- Main App ----------
export default function App() {
  const [user, setUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Track auth and merge with public profile in Firestore
  useEffect(() => {
    let profileUnsub = null;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (profileUnsub) {
        profileUnsub();
        profileUnsub = null;
      }

      if (firebaseUser) {
        try {
          const publicRef = doc(db, "public_profiles", firebaseUser.uid);
          const userRef = doc(db, "users", firebaseUser.uid);

          let publicData = {};
          let userData = {};

          const unsubPublic = onSnapshot(publicRef, (snap) => {
            publicData = snap.exists() ? snap.data() : {};
            setMergedUser();
          });

          const unsubUser = onSnapshot(userRef, (snap) => {
            userData = snap.exists() ? snap.data() : {};
            setMergedUser();
          });

          profileUnsub = () => {
            unsubPublic();
            unsubUser();
          };

          function setMergedUser() {
            setUser({
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              displayName:
                publicData.displayName ??
                firebaseUser.displayName ??
                "",
              photoURL:
                publicData.photoURL ??
                firebaseUser.photoURL ??
                "",
              role: userData.role ?? "user", // 👈 DEFAULT ROLE
              ...publicData,
              ...userData, // role, flags, etc
            });
          }
        } catch (err) {
          console.error("Error setting user listeners:", err);
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName ?? "",
            photoURL: firebaseUser.photoURL ?? "",
            role: "user",
          });
        }
      }
      else {
        setUser(null);
      }
    });

    return () => {
      unsubscribe();
      if (profileUnsub) profileUnsub();
    };
  }, []);

  return (
    <Router>
      {/* give the top-level container a class so CSS can make it full-height flex column */}
      <div className="app-root" style={{ fontFamily: "sans-serif" }}>
        {/* main area that should expand */}
        <main className="app-main">
          <AppContent
            user={user}
            setShowAuthModal={setShowAuthModal}
            showAuthModal={showAuthModal}
          />
        </main>

        {/* footer stays after main and will be pushed to bottom when main grows */}
        <Footer />
      </div>
    </Router>
  );
}
